import { useCallback, useState } from 'react';
import type { CondimentDTO, CondimentSaleDTO, CreateCondimentSaleRequest } from '@six-pockets/shared';
import { CustomerNameField } from '@/features/customers/components/CustomerNameField';
import { ManageFormModal } from '@/features/manage/components/ManageFormModal';
import { formatINR } from '@/utils/format';
import { toItemLines, toItemsPayload, toQtyMap, type QtyMap } from '../utils';
import { CondimentStepperList } from './CondimentStepperList';
import styles from '@/styles/components/SaleFormModal/style.scss';

interface SaleFormModalProps {
  /** Null to open a new tab. */
  sale: CondimentSaleDTO | null;
  catalog: CondimentDTO[];
  busy: boolean;
  error: string | null;
  onSave: (body: CreateCondimentSaleRequest) => void;
  onClose: () => void;
}

/**
 * Open a new tab or add to / change an existing one. Typing the tab name offers the existing customers that match;
 * picking one gives the tab that customer (the bill then starts with them selected). Otherwise it is a plain name.
 * Mount only while open so the fields start fresh.
 */
export function SaleFormModal({ sale, catalog, busy, error, onSave, onClose }: SaleFormModalProps) {
  const [tabName, setTabName] = useState(sale?.tabName ?? '');
  /** The existing customer the name stands for; null once the name is typed over. */
  const [customerId, setCustomerId] = useState(sale?.customerId ?? null);
  const [qty, setQty] = useState<QtyMap>(() => toQtyMap(sale?.items ?? []));
  const setOne = useCallback((id: string, n: number) => setQty((q) => ({ ...q, [id]: n })), []);

  const lines = toItemLines(qty, catalog, sale?.items ?? []);
  const total = lines.reduce((a, l) => a + l.amount, 0);
  const blocked = !tabName.trim() ? 'Enter a tab name' : lines.length === 0 ? 'Add at least one condiment' : undefined;

  return (
    <ManageFormModal
      title={sale ? `Edit ${sale.tabName}'s tab` : 'New tab'}
      submitLabel={sale ? 'Save changes' : 'Open tab'}
      busy={busy}
      error={error}
      blockedReason={blocked}
      onSubmit={() => onSave({ tabName: tabName.trim(), customerId, items: toItemsPayload(qty) })}
      onClose={onClose}
    >
      <CustomerNameField
        label="Tab name"
        placeholder="e.g. Rahul or Rahul's friend"
        hint="Pick an existing customer from the list, or just type a name"
        value={tabName}
        customerId={customerId}
        onChange={(name, id) => {
          setTabName(name);
          setCustomerId(id);
        }}
        data-autofocus
      />
      <CondimentStepperList condiments={catalog} qty={qty} onChange={setOne} showPrice layout="grid" />
      <div className={styles.total}>
        <span>Total</span>
        <span className={styles.money}>{formatINR(total)}</span>
      </div>
    </ManageFormModal>
  );
}
